import {
  FocusLevelSchema,
  GenerationOutputSchema,
  GeneratorParamsSchema,
  InstrumentSchema,
  ProviderCapabilitiesSchema,
  formatIssues,
  type GenerationRequest,
  type GeneratorParams,
} from '../domain/schemas';
import { createId, nowIso } from '../lib/ids';
import { compose, type Composition } from '../music/generator/composer';
import { ProviderError, type GenerationProvider, type GenerationResult, type ProviderAvailability } from './types';

export { mulberry32 } from '../music/random';

type RequestParams = GenerationRequest['params'];

/** Flattens composer parameters into the primitive-only request parameter record. */
export function toRequestParams(params: GeneratorParams): RequestParams {
  return {
    genre: params.genre,
    bpm: params.bpm,
    keyRoot: params.key.root,
    keyScale: params.key.scale,
    mood: params.mood,
    intensity: params.intensity,
    complexity: params.complexity,
    durationSec: params.durationSec,
    seed: params.seed,
    focus: Object.entries(params.focus)
      .map(([instrument, level]) => `${instrument}:${level}`)
      .join(','),
  };
}

/** Inverse of toRequestParams, with full validation. */
export function fromRequestParams(params: RequestParams): GeneratorParams {
  const focus: Record<string, string> = {};
  if (typeof params.focus === 'string' && params.focus) {
    for (const pair of params.focus.split(',')) {
      const [instrument, level] = pair.split(':');
      if (!InstrumentSchema.safeParse(instrument).success || !FocusLevelSchema.safeParse(level).success) {
        throw new ProviderError('invalid-request', `Invalid focus "${pair}".`);
      }
      focus[instrument!] = level!;
    }
  }
  const parsed = GeneratorParamsSchema.safeParse({
    genre: params.genre,
    bpm: params.bpm,
    key: { root: params.keyRoot, scale: params.keyScale },
    mood: params.mood,
    intensity: params.intensity,
    complexity: params.complexity,
    durationSec: params.durationSec,
    seed: params.seed,
    focus,
  });
  if (!parsed.success) throw new ProviderError('invalid-request', `Invalid composition parameters: ${formatIssues(parsed.error).join('; ')}`);
  return parsed.data;
}

/**
 * Local, rule-based procedural composer (not AI). Runs in the browser, deterministically:
 * the same parameters and seed always produce the same composition.
 */
export class LocalProceduralProvider implements GenerationProvider {
  readonly capabilities = ProviderCapabilitiesSchema.parse({
    providerId: 'local-procedural',
    displayName: 'Local procedural composer',
    kinds: ['procedural'],
    local: true,
    requiresConfiguration: false,
    maxPromptLength: 500,
    outputFormats: ['sonicforge-composition'],
    supportsCancellation: false,
  });

  availability(): ProviderAvailability {
    return { state: 'available' };
  }

  async generate(request: GenerationRequest): Promise<GenerationResult & { payload: Composition }> {
    if (request.kind !== 'procedural') {
      throw new ProviderError('invalid-request', `Local procedural provider cannot handle ${request.kind}.`);
    }
    const params = fromRequestParams(request.params);
    const composition = compose(params);
    const output = GenerationOutputSchema.parse({
      id: createId('out'),
      requestId: request.id,
      kind: 'procedural',
      providerId: this.capabilities.providerId,
      createdAt: nowIso(),
      data: { ...toRequestParams(params), bars: composition.bars, durationSec: composition.durationSec },
    });
    return { output, payload: composition };
  }
}
